/**
 * The outputs made so far, under the player (docs/SPEC.md §13, §14.1;
 * P24-T05): each real-time film and ray-traced final, newest first — while it
 * is made its progress (the socket's `output` events) and the last frame
 * rendered (`frame`), with **Stop**; then its download; a failure's sentence.
 * True renders are the true-render panel's.
 */
import { useQuery } from "@tanstack/react-query";
import {
  type Output,
  framesKey,
  running,
  useCancelOutput,
  useOutputs,
} from "../api/outputs";
import { OUTPUTS, outputError, outputLine, say } from "../copy/outputs";
import { formatBytes } from "../ui/format";
import { Button, Card } from "../ui";

function stateLine(o: Output): string {
  if (o.state === "queued") return OUTPUTS.queued;
  if (o.state === "working") {
    return say(OUTPUTS.working, { percent: Math.round(o.progress * 100) });
  }
  if (o.state === "cancelled") return OUTPUTS.cancelled;
  if (o.state === "failed") {
    return o.sentence ?? "The film could not be rendered; nothing was charged.";
  }
  return OUTPUTS.done;
}

function Row({
  output,
  frame,
  onStop,
  stopping,
}: {
  output: Output;
  frame: string | null;
  onStop(): void;
  stopping: boolean;
}) {
  const line = outputLine(output);
  const busy = running(output);
  const facts = [
    output.bytes ? formatBytes(output.bytes) : null,
    output.duration_s
      ? say(OUTPUTS.seconds, { seconds: output.duration_s })
      : null,
    output.credits_captured
      ? say(OUTPUTS.credits, { credits: output.credits_captured })
      : null,
  ].filter(Boolean);
  return (
    <li
      className="flex flex-wrap items-center gap-3 border-t border-slate-100 py-2 first:border-t-0"
      data-testid={`output-${output.id}`}
      data-state={output.state}
    >
      {busy && frame ? (
        <img
          src={frame}
          alt={say(OUTPUTS.frameAlt, { name: line })}
          className="h-12 w-auto rounded bg-slate-100"
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-semibold text-slate-800">{line}</span>
        <span
          className="text-xs text-slate-600"
          role={busy ? "status" : undefined}
        >
          {stateLine(output)}
          {facts.length && output.state === "done"
            ? ` · ${facts.join(" · ")}`
            : ""}
        </span>
        {output.state === "working" ? (
          <div
            role="progressbar"
            aria-label={line}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(output.progress * 100)}
            className="h-1.5 w-full max-w-xs overflow-hidden rounded-pill bg-slate-200"
          >
            <div
              className="h-full rounded-pill bg-brand transition-[width]"
              style={{ width: `${Math.round(output.progress * 100)}%` }}
            />
          </div>
        ) : null}
      </div>
      {busy ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={stopping}
          aria-busy={stopping}
          onClick={onStop}
        >
          {OUTPUTS.stop}
        </Button>
      ) : output.state === "done" && output.url ? (
        <Button as="a" href={output.url} size="sm" variant="secondary">
          {OUTPUTS.download}
        </Button>
      ) : null}
    </li>
  );
}

export function Outputs({ adId }: { adId: string }) {
  const outputs = useOutputs(adId);
  const cancel = useCancelOutput(adId);
  const frames = useQuery<Record<string, string>>({
    queryKey: framesKey(adId),
    queryFn: () => ({}),
    enabled: false,
  });
  const items = (outputs.data?.items ?? []).filter((o) => o.kind !== "still");
  if (items.length === 0) return null;
  return (
    <Card className="flex flex-col gap-2 p-4" data-testid="outputs">
      <h2 className="m-0 text-sm font-semibold text-slate-800">
        {OUTPUTS.title}
      </h2>
      <ul className="m-0 list-none p-0">
        {items.map((o) => (
          <Row
            key={o.id}
            output={o}
            frame={frames.data?.[o.id] ?? null}
            stopping={cancel.isPending && cancel.variables === o.id}
            onStop={() => cancel.mutate(o.id)}
          />
        ))}
      </ul>
      {cancel.isError ? (
        <p className="m-0 text-sm text-slate-700" role="alert">
          {outputError(cancel.error)}
        </p>
      ) : null}
      <p className="m-0 text-xs text-slate-500">{OUTPUTS.inMedia}</p>
    </Card>
  );
}
